import { LinkRules } from '@platejs/link'
import {
  BulletedListRules,
  OrderedListRules,
  unwrapList,
} from '@platejs/list-classic'
import { ImagePlugin, MediaEmbedPlugin } from '@platejs/media/react'
import { createBlockStartInputRule, createMarkInputRule } from 'platejs'
import type { TElement, Value } from 'platejs'
import {
  createPlateEditor,
  PlateElement,
  useFocused,
  useSelected,
} from 'platejs/react'
import type { PlateEditor, PlateElementProps } from 'platejs/react'

import {
  HeadingElement,
  RichTextFieldBoldPlugin,
  RichTextFieldH2Plugin,
  RichTextFieldItalicPlugin,
  RichTextFieldParagraphPlugin,
  RichTextFieldUnderlinePlugin,
  createRichTextFieldBoldInputRule,
  createRichTextFieldItalicInputRule,
  createRichTextFieldLinkPlugin,
} from '@buffer-mono/popcorn/rich-text'
import {
  RichTextFieldListPlugin,
  RichTextFieldBulletedListPlugin,
  RichTextFieldNumberedListPlugin,
  RichTextFieldListItemPlugin,
  RichTextFieldListItemContentPlugin,
} from '@buffer-mono/popcorn/rich-text/lists'

import styles from './ChangelogContentEditor.module.css'

import type { JSX } from 'react'

export type ChangelogValue = Value
export type ChangelogEditor = PlateEditor

function ChangelogHeadingElement(props: PlateElementProps): JSX.Element {
  return <HeadingElement {...props} as="h2" className={styles.heading} />
}

function ImageElementComponent(props: PlateElementProps): JSX.Element {
  const selected = useSelected()
  const focused = useFocused()
  const url = typeof props.element.url === 'string' ? props.element.url : ''

  return (
    <PlateElement
      {...props}
      className={`${styles.imageWrapper} ${selected && focused ? styles.selected : ''}`}
    >
      <div contentEditable={false}>
        <img src={url} alt="" className={styles.image} />
      </div>
      {props.children}
    </PlateElement>
  )
}

function MediaEmbedElementComponent(props: PlateElementProps): JSX.Element {
  const selected = useSelected()
  const focused = useFocused()
  const url = typeof props.element.url === 'string' ? props.element.url : ''
  const canRender = isAllowedMediaUrl(url)

  return (
    <PlateElement
      {...props}
      className={`${styles.mediaWrapper} ${selected && focused ? styles.selected : ''}`}
    >
      {canRender && (
        <div contentEditable={false}>
          <iframe
            src={url}
            title="Embedded video"
            className={styles.mediaIframe}
            allowFullScreen
            sandbox="allow-scripts"
          />
        </div>
      )}
      {props.children}
    </PlateElement>
  )
}

export function isAllowedMediaUrl(url: string): boolean {
  try {
    return ['http:', 'https:'].includes(new URL(url).protocol)
  } catch {
    return false
  }
}

export const emptyChangelogValue: ChangelogValue = [
  {
    type: 'p',
    children: [{ text: '' }],
  },
]

export const changelogTemplateValue: ChangelogValue = [
  {
    type: 'p',
    children: [{ text: 'Describe the new feature or update here...' }],
  },
  {
    type: 'p',
    children: [{ text: '' }],
  },
  {
    type: 'h2',
    children: [{ text: '🐛 Bug Fixes' }],
  },
  {
    type: 'ul',
    children: [
      {
        type: 'li',
        children: [
          {
            type: 'lic',
            children: [{ text: 'Fixed...' }],
          },
        ],
      },
    ],
  },
  {
    type: 'p',
    children: [{ text: '' }],
  },
  {
    type: 'h2',
    children: [{ text: '✨ Improvements' }],
  },
  {
    type: 'ul',
    children: [
      {
        type: 'li',
        children: [
          {
            type: 'lic',
            children: [{ text: 'Improved...' }],
          },
        ],
      },
    ],
  },
]

/**
 * Creates the changelog editor.
 * @see serializeNode for the supported HTML elements and marks.
 */
export const createChangelogEditor = (
  value: ChangelogValue = emptyChangelogValue,
): ChangelogEditor =>
  createPlateEditor({
    value,
    nodeId: false,
    plugins: [
      RichTextFieldParagraphPlugin,
      RichTextFieldBoldPlugin.configure({
        inputRules: [
          createRichTextFieldBoldInputRule(),
          createRichTextFieldBoldInputRule({ variant: '_' }),
        ],
      }),
      RichTextFieldItalicPlugin.configure({
        inputRules: [
          createRichTextFieldItalicInputRule(),
          createRichTextFieldItalicInputRule({ variant: '_' }),
        ],
      }),
      RichTextFieldUnderlinePlugin.configure({
        inputRules: [
          createMarkInputRule({ start: '~~', end: '~', trigger: '~' }),
        ],
      }),
      RichTextFieldH2Plugin.withComponent(ChangelogHeadingElement).configure({
        inputRules: [
          createBlockStartInputRule({
            match: /^#{1,2}$/,
            trigger: ' ',
            apply: ({ editor }, match) => {
              editor.tf.delete({ at: match.range })
              unwrapList(editor)
              editor.tf.setNodes({ type: 'h2' })
              return true
            },
          }),
        ],
      }),
      RichTextFieldListPlugin.extend({
        plugins: [
          RichTextFieldBulletedListPlugin.configure({
            inputRules: [
              BulletedListRules.markdown(),
              BulletedListRules.markdown({ variant: '*' }),
            ],
          }),
          RichTextFieldNumberedListPlugin.configure({
            inputRules: [
              OrderedListRules.markdown(),
              OrderedListRules.markdown({ variant: ')' }),
            ],
          }),
          RichTextFieldListItemPlugin,
          RichTextFieldListItemContentPlugin,
        ],
      }),
      createRichTextFieldLinkPlugin({
        allowedSchemes: ['http', 'https', 'mailto'],
        isUrl: (text) => {
          try {
            return ['http:', 'https:', 'mailto:'].includes(
              new URL(text).protocol,
            )
          } catch {
            return false
          }
        },
      }).configure({
        options: { keepSelectedTextOnPaste: true },
        inputRules: [
          LinkRules.autolink({ variant: 'space' }),
          LinkRules.autolink({ variant: 'paste' }),
          LinkRules.autolink({ variant: 'break' }),
        ],
      }),
      ImagePlugin.withComponent(ImageElementComponent).configure({
        options: { disableUploadInsert: true },
      }),
      MediaEmbedPlugin.withComponent(MediaEmbedElementComponent),
    ],
  })

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isTextLeaf(node: unknown): node is {
  text: string
  bold?: boolean
  italic?: boolean
  underline?: boolean
} {
  return isRecord(node) && typeof node.text === 'string'
}

function serializeLeaf(leaf: {
  text: string
  bold?: boolean
  italic?: boolean
  underline?: boolean
}): string {
  let html = escapeHtml(leaf.text).replace(/\r?\n/g, '<br>')
  if (leaf.bold) html = `<strong>${html}</strong>`
  if (leaf.italic) html = `<em>${html}</em>`
  if (leaf.underline) html = `<u>${html}</u>`
  return html
}

function serializeNode(node: unknown): string {
  if (isTextLeaf(node)) return serializeLeaf(node)
  if (!isRecord(node)) return ''

  const children = (Array.isArray(node.children) ? node.children : [])
    .map(serializeNode)
    .join('')
  const type = typeof node.type === 'string' ? node.type : undefined
  const url = typeof node.url === 'string' ? node.url : ''
  switch (type) {
    case 'p':
      return `<p>${children}</p>`
    case 'h2':
      return `<h2>${children}</h2>`
    case 'ul':
      return `<ul>${children}</ul>`
    case 'ol':
      return `<ol>${children}</ol>`
    case 'li':
      return `<li>${children}</li>`
    case 'lic':
      // List-item content wrapper — emit children only.
      return children
    case 'a':
      return `<a href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer">${children}</a>`
    case 'img': {
      // Drop data: URLs — image inserts upload via the editor and store S3
      // URLs. A data: URL here would inflate `content` past the gateway's
      // 100 KB JSON body limit.
      if (url.startsWith('data:')) return ''
      return `<img src="${escapeHtml(url)}" alt="" />`
    }
    case 'media_embed':
      if (!isAllowedMediaUrl(url)) return ''
      return `<iframe src="${escapeHtml(url)}" frameborder="0" allowfullscreen sandbox="allow-scripts"></iframe>`
    default:
      // Unknown element — emit children so we don't drop data silently.
      return children
  }
}

/**
 * Serializes the HTML contract used by changelog storage and previews.
 * @see serializeNode for the supported elements and marks.
 */
export function serializeChangelogToHtml(nodes: ChangelogValue): string {
  return nodes.map(serializeNode).join('')
}

/**
 * Converts stored HTML to the editor value for an existing entry.
 * @param html - The stored HTML.
 * @returns The editor value.
 */
export function deserializeChangelogHtml(html: string): ChangelogValue {
  if (!html.trim()) return structuredClone(emptyChangelogValue)

  const editor = createChangelogEditor()
  const nodes = editor.api.html.deserialize({ element: html })
  const elements = nodes.flatMap((node): TElement[] => {
    if ('text' in node) return [{ type: 'p', children: [node] }]
    if (
      node.type === 'media_embed' &&
      (typeof node.url !== 'string' || !isAllowedMediaUrl(node.url))
    ) {
      return []
    }
    return [node]
  })
  return elements.length ? elements : structuredClone(emptyChangelogValue)
}
